/**
 * Schémas pour le module Entretiens Vidéo (V10 — mai 2026).
 *
 * Une entreprise propose un créneau d'entretien visio à un candidat qui
 * l'intéresse. L'entreprise fournit son propre outil (Google Meet, Teams,
 * Zoom, etc.). Le Comptoir gère uniquement : invitation, sélection de
 * créneau et notification par email.
 *
 * Workflow :
 *   1. Entreprise crée l'invitation (lien + créneaux + message optionnel).
 *   2. Candidat accepte un créneau ou décline.
 *   3. Email de confirmation aux 2 parties.
 */
import { z } from 'zod';
import { uuidSchema } from './common.js';
// ─── Référentiels ────────────────────────────────────────────────────────
export const INTERVIEW_PLATFORMS = [
    'google_meet',
    'teams',
    'zoom',
    'other',
];
export const INTERVIEW_PLATFORM_LABELS = {
    google_meet: 'Google Meet',
    teams: 'Microsoft Teams',
    zoom: 'Zoom',
    other: 'Autre outil',
};
export const INTERVIEW_STATUSES = [
    'pending',
    'accepted',
    'declined',
    'cancelled',
];
export const INTERVIEW_STATUS_LABELS = {
    pending: 'En attente de réponse',
    accepted: 'Créneau confirmé',
    declined: 'Décliné',
    cancelled: 'Annulé',
};
// ─── Création (entreprise → candidat) ────────────────────────────────────
export const interviewCreateSchema = z.object({
    studentUserId: uuidSchema,
    jobApplicationId: uuidSchema.optional(),
    jobOfferId: uuidSchema.optional(),
    platform: z.enum(INTERVIEW_PLATFORMS),
    videoLink: z
        .string()
        .trim()
        .url('Le lien doit être une URL valide (https://…).')
        .refine((url) => {
        try {
            return new URL(url).protocol === 'https:';
        }
        catch {
            return false;
        }
    }, 'Le lien doit commencer par https:// (connexion sécurisée requise).'),
    proposedSlots: z
        .array(z.coerce.date())
        .min(1, 'Proposez au moins un créneau.')
        .max(4, 'Maximum 4 créneaux proposés.')
        .refine((slots) => slots.every((d) => d > new Date()), 'Tous les créneaux doivent être dans le futur.'),
    companyMessage: z.string().trim().max(1000).optional().or(z.literal('')),
});
// ─── Réponse candidat ────────────────────────────────────────────────────
/** Le candidat accepte l'invitation et sélectionne un créneau. */
export const interviewAcceptSchema = z.object({
    selectedSlot: z.coerce.date(),
    studentMessage: z.string().trim().max(500).optional().or(z.literal('')),
});
/** Le candidat décline. */
export const interviewDeclineSchema = z.object({
    studentMessage: z.string().trim().max(500).optional().or(z.literal('')),
});
//# sourceMappingURL=interview.js.map